import { useMemo, useState } from 'react';

type MessePaymentStatus = 'completed' | 'pending' | 'failed';

export type MessePayment = {
  id: string;
  reference: string;
  client: {
    id?: number | null;
    name: string;
    email?: string | null;
    phone?: string | null;
    paroisse?: string | null;
    diocese_prefere?: string | null;
  };
  eglise: {
    id?: number | null;
    name: string;
    ville?: string | null;
    diocese?: string | null;
  };
  type: string;
  intention: string;
  repetition?: string | null;
  nb_messes: number;
  occurrences: Array<{ type?: string; date?: string; heure?: string; intention?: string }>;
  date?: string | null;
  heure?: string | null;
  amount: number;
  payment_method: string;
  status: MessePaymentStatus;
  payment_status: string;
  payment_token?: string | null;
  checkout_url?: string | null;
  receipt_url?: string | null;
  transaction_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type MessePaymentStats = {
  revenue: number;
  pending: number;
  failed: number;
  payments: number;
  completed: number;
  pending_count: number;
  failed_count: number;
};

const formatMoney = (value: number) => `${value.toLocaleString('fr-FR')} FCFA`;
const formatDate = (value?: string | null) => {
  if (!value) return 'Non renseigné';
  return new Date(value).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatMesseDay = (value?: string | null) => {
  if (!value) return 'Non renseigné';
  return new Date(`${value}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const uniqueValues = (values: Array<string | null | undefined>) =>
  Array.from(new Set(values.map((value) => value?.trim()).filter(Boolean) as string[]));

const messeOccurrences = (payment: MessePayment) =>
  payment.occurrences.length
    ? payment.occurrences
    : [{ type: payment.type, date: payment.date || undefined, heure: payment.heure || undefined, intention: payment.intention }];

const messeDaysLabel = (payment: MessePayment) => {
  const days = uniqueValues(messeOccurrences(payment).map((occurrence) => occurrence.date)).map(formatMesseDay);
  return days.length ? days.join(' | ') : 'Non renseigné';
};

const messeHoursLabel = (payment: MessePayment) => {
  const hours = uniqueValues(messeOccurrences(payment).map((occurrence) => occurrence.heure));
  return hours.length ? hours.join(' | ') : 'Non renseigné';
};

const messeIntentionsLabel = (payment: MessePayment) => {
  const intentions = uniqueValues([payment.intention, ...messeOccurrences(payment).map((occurrence) => occurrence.intention)]);
  return intentions.length ? intentions.join(' | ') : 'Non renseigné';
};

const paymentMethodLabel = (value: string) => {
  if (value === 'orange_money') return 'Orange Money';
  if (value === 'wave') return 'Wave';
  if (value === 'mixx_by_yas') return 'Mixx by Yas';
  if (value === 'dexpay') return 'DexPay';
  if (value === 'mobile_money') return 'Mobile Money';
  return value.replace(/_/g, ' ');
};

const statusBadgeClass: Record<MessePaymentStatus, string> = {
  completed: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
};

const statusLabel: Record<MessePaymentStatus, string> = {
  completed: 'Paiement réussi',
  pending: 'Paiement non validé',
  failed: 'Échoué',
};

const methodClass = (value: string) => {
  if (value === 'orange_money') return 'bg-orange-100 text-orange-700';
  if (value === 'wave') return 'bg-indigo-100 text-indigo-700';
  if (value === 'mixx_by_yas') return 'bg-yellow-100 text-yellow-800';
  if (value === 'dexpay') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-600';
};

export default function AdminMessePayments({
  stats,
  payments,
}: {
  stats: MessePaymentStats;
  payments: MessePayment[];
}) {
  const [statusFilter, setStatusFilter] = useState<'all' | MessePaymentStatus>('all');
  const [search, setSearch] = useState('');
  const [selectedPayment, setSelectedPayment] = useState<MessePayment | null>(null);
  const completedPayments = useMemo(
    () => payments.filter((payment) => payment.status === 'completed' && payment.payment_status === 'completed'),
    [payments]
  );

  const filteredPayments = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return payments.filter((payment) => {
      const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;
      const haystack = [
        payment.reference,
        payment.client.name,
        payment.client.email,
        payment.client.phone,
        payment.eglise.name,
        payment.eglise.ville,
        payment.eglise.diocese,
        payment.type,
        payment.intention,
        messeDaysLabel(payment),
        messeHoursLabel(payment),
        payment.transaction_id,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return matchesStatus && (!normalizedSearch || haystack.includes(normalizedSearch));
    });
  }, [payments, search, statusFilter]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          { label: 'Montant encaissé', value: formatMoney(stats.revenue), icon: 'ri-hand-heart-line', color: 'bg-emerald-100 text-emerald-700' },
          { label: 'Demandes payées', value: String(completedPayments.length), icon: 'ri-file-list-3-line', color: 'bg-navy/10 text-navy' },
          { label: 'Paiements non validés', value: String(stats.pending_count), icon: 'ri-error-warning-line', color: 'bg-amber-100 text-amber-700' },
          { label: 'Paiements échoués', value: String(stats.failed_count), icon: 'ri-close-circle-line', color: 'bg-red-100 text-red-700' },
        ].map((card) => (
          <div key={card.label} className="rounded-2xl border border-[#eef1f5] bg-white p-5 shadow-[0_8px_24px_rgba(15,23,42,0.04)]">
            <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${card.color}`}>
              <i className={`${card.icon} text-lg`}></i>
            </div>
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className="mt-1 text-2xl font-bold text-navy">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#eef1f5] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col gap-3 border-b border-[#eef1f5] px-5 py-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100">
              <i className="ri-church-line text-amber-700 text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Paiements des demandes de messe</h3>
              <p className="text-xs text-gray-400">{filteredPayments.length} paiement(s)</p>
            </div>
          </div>

          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="flex flex-wrap items-center gap-1 rounded-xl bg-[#f5f7fa] p-1">
              {([
                { key: 'all', label: 'Tous' },
                { key: 'completed', label: 'Paiement réussi' },
                { key: 'pending', label: 'Paiement non validé' },
                { key: 'failed', label: 'Échoué' },
              ] as const).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setStatusFilter(item.key)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${statusFilter === item.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Client, église, référence..."
                className="w-full rounded-xl border border-[#e5e7eb] bg-white py-2.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 xl:w-72"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1480px]">
            <thead>
              <tr className="border-b border-[#f2f4f7] bg-[#fcfcfd]">
                {['Référence', 'Client', 'Église', 'Type', 'Heures messe', 'Jour(s) messe', 'Intention(s)', 'Messes', 'Montant', 'Paiement', 'Statut', 'Date paiement', 'Détails'].map((label) => (
                  <th key={label} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="border-b border-[#f4f6f8] transition-colors hover:bg-[#fafbfd]">
                  <td className="px-4 py-4 text-xs font-semibold text-gold-dark">{payment.reference}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">{payment.client.name}</p>
                    <p className="text-xs text-gray-400">{payment.client.phone || payment.client.email || 'Contact non renseigné'}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">{payment.eglise.name}</p>
                    <p className="text-xs text-gray-400">{[payment.eglise.ville, payment.eglise.diocese].filter(Boolean).join(' · ') || 'Localisation non renseignée'}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{payment.type}</td>
                  <td className="px-4 py-3 text-xs font-semibold text-gray-700">{messeHoursLabel(payment)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{messeDaysLabel(payment)}</td>
                  <td className="max-w-[240px] px-4 py-3 text-xs text-gray-600">
                    <p className="line-clamp-3">{messeIntentionsLabel(payment)}</p>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{payment.nb_messes}</td>
                  <td className="px-4 py-3 text-sm font-bold text-navy">{formatMoney(payment.amount)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${methodClass(payment.payment_method)}`}>
                      {paymentMethodLabel(payment.payment_method)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusBadgeClass[payment.status]}`}>
                      {statusLabel[payment.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{formatDate(payment.created_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedPayment(payment)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-navy px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-navy-light"
                    >
                      <i className="ri-eye-line"></i>
                      Voir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredPayments.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-gray-400">
              Aucun paiement de demande de messe trouvé.
            </div>
          )}
        </div>
      </div>

      {selectedPayment ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-3xl overflow-hidden rounded-[26px] bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
              <div>
                <h3 className="text-xl font-bold text-navy">{selectedPayment.reference}</h3>
                <p className="text-sm text-gray-400">Détails complets du paiement de demande de messe</p>
              </div>
              <button onClick={() => setSelectedPayment(null)} className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="max-h-[72vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <DetailBlock title="Client" items={[
                  ['Nom', selectedPayment.client.name],
                  ['Email', selectedPayment.client.email || 'Non renseigné'],
                  ['Téléphone', selectedPayment.client.phone || 'Non renseigné'],
                  ['Paroisse', selectedPayment.client.paroisse || 'Non renseignée'],
                ]} />
                <DetailBlock title="Église" items={[
                  ['Nom', selectedPayment.eglise.name],
                  ['Ville', selectedPayment.eglise.ville || 'Non renseignée'],
                  ['Diocèse', selectedPayment.eglise.diocese || 'Non renseigné'],
                ]} />
                <DetailBlock title="Paiement" items={[
                  ['Montant', formatMoney(selectedPayment.amount)],
                  ['Méthode', paymentMethodLabel(selectedPayment.payment_method)],
                  ['Statut', statusLabel[selectedPayment.status]],
                  ['Transaction', selectedPayment.transaction_id || 'Non renseignée'],
                  ['Token', selectedPayment.payment_token || 'Non renseigné'],
                  ['Créé le', formatDate(selectedPayment.created_at)],
                  ['Mis à jour le', formatDate(selectedPayment.updated_at)],
                ]} />
                <DetailBlock title="Demande" items={[
                  ['Type', selectedPayment.type],
                  ['Répétition', selectedPayment.repetition === 'multiple' ? 'Plusieurs messes' : 'Une seule messe'],
                  ['Nombre', String(selectedPayment.nb_messes)],
                  ['Jour(s)', messeDaysLabel(selectedPayment)],
                  ['Heure(s)', messeHoursLabel(selectedPayment)],
                  ['Intention(s)', messeIntentionsLabel(selectedPayment)],
                ]} />
              </div>

              <div className="mt-4 rounded-2xl bg-[#f8f9fc] p-4">
                <h4 className="mb-3 text-sm font-bold text-gray-900">Dates demandées</h4>
                <div className="space-y-2">
                  {messeOccurrences(selectedPayment).map((occurrence, index) => (
                    <div key={`${occurrence.date}-${occurrence.heure}-${index}`} className="flex items-center justify-between rounded-xl bg-white px-4 py-3 text-sm">
                      <span className="font-semibold text-gray-900">{occurrence.type || selectedPayment.type}</span>
                      <span className="text-gray-500">{formatMesseDay(occurrence.date)} · {occurrence.heure || 'Heure non renseignée'}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {selectedPayment.checkout_url ? (
                  <a href={selectedPayment.checkout_url} target="_blank" rel="noreferrer" className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white">
                    Ouvrir le checkout
                  </a>
                ) : null}
                {selectedPayment.receipt_url ? (
                  <a href={selectedPayment.receipt_url} target="_blank" rel="noreferrer" className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">
                    Voir le reçu
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DetailBlock({ title, items }: { title: string; items: Array<[string, string]> }) {
  return (
    <div className="rounded-2xl bg-[#f8f9fc] p-4">
      <h4 className="mb-3 text-sm font-bold text-gray-900">{title}</h4>
      <div className="space-y-2">
        {items.map(([label, value]) => (
          <div key={label} className="flex gap-3 text-sm">
            <span className="w-28 flex-shrink-0 text-gray-400">{label}</span>
            <span className="min-w-0 flex-1 break-words font-semibold text-gray-800">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
